/* eslint-disable consistent-return */
import { createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "axios";
import { toast } from "utils/toast";

import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";

import DealerCare from "services/dealerCareApi";
import Billing from "services/billingApi";

import i18next from "i18next";
import {
  DealerCareSearchRequest,
  DealerCareSearchResponse,
  UpdateDealerCareDetailsRequest,
  DealerSearchByIdResponse,
  DealerSearchByIdRequest,
  UpdateDealerRequestProps,
  EnableDealerRequestProps,
  DisableDealerRequestProps,
} from "./types.store";

export const handleGetDealerCareRequest = createAsyncThunk<
  DealerCareSearchResponse,
  DealerCareSearchRequest
>("getDealerCare", async (params, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const query = Object.entries(params)
      .map(([key, val]) => `${key}=${val}`)
      .join("&");
    const term = params?.term
      ? `&term=${params.term.replace(/\s+/g, " ")}`
      : "";

    const finalQuery = query.includes("term") ? query : `${query}${term}`;
    const { data } = await DealerCare.get(`/v1/dealers/search?${finalQuery}`);

    return data;
  } catch (err) {
    toast("Falha ao carregar as revendas, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }

    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateDealerCareDetailsRequest = createAsyncThunk<
  null,
  UpdateDealerCareDetailsRequest
>("updateDealerCareDetails", async (params, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));
    await DealerCare.put(`/v1/dealers/${params.dealerId}/details`, {
      ...params,
    });
    return null;
  } catch (err) {
    toast("Falha ao atualizar revenda, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleSearchDealerByIdRequest = createAsyncThunk<
  DealerSearchByIdResponse,
  DealerSearchByIdRequest
>("getSearchDealer", async (params, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const dealerInfo = await DealerCare.get(`/v1/dealers/${params.dealerId}`);
    const dealerPaymentMethods = await Billing.get(
      `/v2/dealers/${params.dealerId}/paymentmethods-all-origins`,
    );

    return {
      dealerInfo: dealerInfo.data.data,
      paymentMethods: dealerPaymentMethods.data,
    };
  } catch (err) {
    toast("Falha ao carregar os dados da revenda, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateDealerRequest = createAsyncThunk<
  void,
  UpdateDealerRequestProps
>("postPutUpdateDealer", async (params, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const updateDealerTradeName = await DealerCare.put(
      `/v1/dealers/${params.dealerId}/tradename`,
      {
        tradeName: params.tradeName,
        whatsAppNumber: params.whatsAppNumber,
      },
    );
    const updateDealerPaymentMethods = await Billing.post(
      `/v2/dealers/${params.dealerId}/paymentmethods`,
      params.methodsPayments,
    );

    await Promise.all([updateDealerTradeName, updateDealerPaymentMethods]);
  } catch (err) {
    toast("Falha ao atualizar os dados da revenda, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleEnableDealerRequest = createAsyncThunk<
  void,
  EnableDealerRequestProps
>("putEnableDealer", async (body, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await DealerCare.put(`/v1/dealers/${body.dealerId}/enable`, body);
  } catch (err) {
    toast(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore

      err?.response?.data?.errors?.[0]?.message ||
        i18next.t("failedEnableDealerPleaseAgain").toString(),
      {
        type: "error",
      },
    );

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleDisableDealerRequest = createAsyncThunk<
  void,
  DisableDealerRequestProps
>("putDisableDealer", async (body, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await DealerCare.put(`/v1/dealers/${body.dealerId}/disable`, body);
  } catch (err) {
    toast(i18next.t("failedDisableDealerPleaseAgain").toString(), {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});
